/**
 * useContextMenu.ts - Hook for managing context menu state
 * 
 * Migrated from Viewr with enhancements
 * 
 * CHANGE HISTORY:
 * - 251115_1822 [current] danielbeer: Migrated from Viewr
 * - 251101_0845 [viewr] danielbeer: Added TypeScript generics
 */

import { useState, useCallback } from 'react';
import type React from 'react';
import type { ContextMenuState } from './types';

export const useContextMenu = <T = any>() => {
  const [state, setState] = useState<ContextMenuState<T>>({
    isOpen: false,
    position: null,
    data: null,
  });

  /**
   * Open context menu at position
   * @param event - The mouse event (right-click)
   * @param data - Optional data to associate with menu (e.g., clicked item)
   */
  const openContextMenu = useCallback((event: React.MouseEvent | MouseEvent, data: T | null = null) => {
    event.preventDefault();
    event.stopPropagation();

    // If menu is already open at this position, close it (toggle behavior)
    if (state.isOpen && 
        state.position?.x === event.clientX && 
        state.position?.y === event.clientY) {
      setState({
        isOpen: false,
        position: null,
        data: null,
      });
      return;
    }

    setState({
      isOpen: true,
      position: {
        x: event.clientX,
        y: event.clientY,
      },
      data,
    });
  }, [state]);

  /**
   * Close context menu
   */
  const closeContextMenu = useCallback(() => {
    setState({
      isOpen: false,
      position: null,
      data: null,
    });
  }, []);

  return {
    isOpen: state.isOpen,
    position: state.position,
    data: state.data,
    openContextMenu,
    closeContextMenu,
  };
};
